import type { PackageStatus } from "~/utils/allocationPackageLifecycle"
import type { STORE_FORMATS } from "~/utils/allocationStores"

export type StoreClassification = "A" | "B" | "C" | "D" | "Outlet"
export type StoreType = (typeof STORE_FORMATS)[number]["label"]
export type StoreSize = "Small" | "Medium" | "Large"

export type StoreZone =
  | "DACH"
  | "Western Europe"
  | "Southern Europe"
  | "Eastern Europe"
  | "Nordics"
  | "UK"
  | "North America"
  | "Latin America"
  | "Asia Pacific"
  | "South Asia"
  | "Middle East"
  | "Africa"

export type Store = {
  id: string
  storeNumber: string
  city: string
  country: string
  size: string
  floorArea: number
  type: string
  classification: StoreClassification
  sales3mo: number
  rateOfSale: number
  stockCover: number
  sellThrough: number
  salesVsTarget: number | null
  inTransit: number
  storeStock: number
  capacity: number
  replenishmentInterval: string
  transitionTime: string
  isNewStore: boolean
  proxyStoreId: string | null
  address: string
  lat: number
  lng: number
}

export type ProductLifecycle = "NOOS" | "Fashion"
export type ProductStatus = "LIVE" | "CLR"

export type Product = {
  id: string
  name: string
  category: string
  family: string
  color: string | null
  sizeNames: string[]
  lifecycle: ProductLifecycle
  status: ProductStatus
  networkStock: number
  warehouseStock: number
  storeStock: number
  sales: number
  stockCover: number
  sellThrough: number
  salesVsTarget: number
  belowMinimum: number
  sizes: ProductSize[]
}

export type ProductSize = {
  size: string
  storeStock: number
  warehouseStock: number
  recommended: number
  adjusted: number
}

export type RangePackage = {
  id: string
  name: string
  status: PackageStatus
  type: "noos" | "seasonal"
  productIds: string[]
  skuIds?: string[]
  storeIds: string[]
  startDate: string | null
  cutoffDate: string | null
}

export type CartItem = {
  id: string
  productId: string
  productName: string
  storeId: string
  storeNumber: string
  quantity: number
  warehouseAvailable: number
}

// STO statuses per docs/ALLOCATION_PLANNING_TERMINOLOGY.md (ts_open / ts_in_transit / ts_received).
export type TransferStatus = "Open" | "In transit" | "Received" | "Cancelled"

export type Transfer = {
  id: string
  storeId: string
  storeNumber: string
  status: TransferStatus
  productCount: number
  unitCount: number
  createdDate: string
  expectedDelivery: string
}

export type StockHealth = "all" | "healthy" | "critical" | "low" | "overstock"
export type AllocationStatus = "needs-replenishment" | "healthy" | "overstock"

export const CLASSIFICATIONS: StoreClassification[] = ["A", "B", "C", "D", "Outlet"]

export const COUNTRY_TO_ZONE: Record<string, StoreZone> = {
  Germany: "DACH",
  Austria: "DACH",
  Switzerland: "DACH",
  France: "Western Europe",
  Netherlands: "Western Europe",
  Belgium: "Western Europe",
  Ireland: "Western Europe",
  Spain: "Southern Europe",
  Italy: "Southern Europe",
  Portugal: "Southern Europe",
  Greece: "Southern Europe",
  Turkey: "Southern Europe",
  Poland: "Eastern Europe",
  "Czech Republic": "Eastern Europe",
  Denmark: "Nordics",
  Sweden: "Nordics",
  Norway: "Nordics",
  Finland: "Nordics",
  "United Kingdom": "UK",
  "United States": "North America",
  Canada: "North America",
  Mexico: "North America",
  Brazil: "Latin America",
  Japan: "Asia Pacific",
  "South Korea": "Asia Pacific",
  China: "Asia Pacific",
  Singapore: "Asia Pacific",
  Australia: "Asia Pacific",
  Thailand: "Asia Pacific",
  India: "South Asia",
  UAE: "Middle East",
  "South Africa": "Africa",
}
